import * as Sentry from '@sentry/react';
import { type BreadcrumbItem, toNavigationTrail } from '@vizit/utils';
import type { Router } from '../../';
import { getCollectionHref } from '../breadcrumbs/collection-breadcrumbs';

/**
 * Navigates to a new route with breadcrumb trail metadata.
 *
 * This function stores the breadcrumb trail in location.state, which preserves
 * the full navigation history across multiple levels of navigation.
 *
 * Use this when navigating to pages that need dynamic breadcrumbs (e.g., Library pages
 * that can be accessed from multiple entry points).
 *
 * @param router - The TanStack router instance
 * @param navigateParams - Navigation parameters
 * @param navigateParams.to - Destination route path
 * @param navigateParams.params - URL parameters
 * @param navigateParams.search - Search parameters
 * @param navigateParams.from - Breadcrumb trail from the current page
 * @returns The navigate function promise
 *
 * @example
 * ```tsx
 * // Navigate from Home page to Asset details (single level)
 * navigateWithState(router, {
 *   to: '/library/assets/$id',
 *   params: { id: assetId },
 *   from: { trail: [{ label: 'Home', href: '/' }] }
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Navigate from PDP to Asset (preserving trail)
 * const currentBreadcrumbs = useBreadcrumbs();
 * navigateWithState(router, {
 *   to: '/library/assets/$id',
 *   params: { id },
 *   from: { trail: currentBreadcrumbs.slice(0, -1) } // Exclude current page
 * });
 * ```
 */
function navigateWithState(
  router: Router,
  navigateParams: {
    to: string;
    params?: Record<string, unknown>;
    search?: Record<string, unknown> | ((prev: Record<string, unknown>) => void);
    from?: { trail: BreadcrumbItem[] };
  },
) {
  Sentry.addBreadcrumb({
    message: 'Navigating with state',
    level: 'info',
    data: { to: navigateParams.to, has_breadcrumb_trail: !!navigateParams.from },
  });

  const { to, params, search, from } = navigateParams;

  return router.navigate({
    to,
    params,
    search,
    state: from ? { from: { trail: toNavigationTrail(from.trail) } } : undefined,
  });
}

/**
 * Goes back from a collection's child page (Compare, Explore) to the collection, carrying the benchmark picked on it.
 * With the collection behind it in history it steps back, so history keeps one entry for the collection; back restores
 * the collection's URL from before, so a benchmark picked since then replaces the one in it. Otherwise it opens it.
 */
function navigateBackToCollection(
  router: Router,
  { workspaceSlug, collectionId, benchmark }: { workspaceSlug: string; collectionId: string; benchmark?: string },
) {
  const collectionHref = getCollectionHref(workspaceSlug, collectionId);
  const trail: BreadcrumbItem[] = router.state.location.state?.from?.trail ?? [];
  const collectionCrumb = trail.find((crumb) => crumb.href === collectionHref);
  if (!collectionCrumb || !router.history.canGoBack()) {
    return router.navigate({
      to: '/w/$workspaceSlug/library/collections/$collectionId',
      params: { workspaceSlug, collectionId },
      search: benchmark ? { benchmark } : {},
    });
  }
  const collectionSearch = collectionCrumb.search ?? {};
  if (collectionSearch.benchmark !== benchmark) {
    const unsubscribe = router.history.subscribe(() => {
      unsubscribe();
      void router.navigate({
        to: collectionHref,
        search: { ...collectionSearch, benchmark },
        replace: true,
        state: router.history.location.state,
      });
    });
  }
  router.history.back();
}

export { navigateBackToCollection, navigateWithState };
